import { z } from 'zod'

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  remember_me: z.boolean().optional().default(false),
})

export const userSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters').optional(),
  role: z.string().min(1, 'Role is required'),
})

// User management form schema
export const createUserSchema = z.object({
  user_login: z.string().min(1, 'Username is required'),
  user_email: z.string().email('Invalid email address'),
  user_pass: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one digit')
    .regex(/[@$!%*#?&]/, 'Password must contain at least one special character (@$!%*#?&)'),
  employee_id: z.string().min(1, 'Employee ID is required'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  position: z.string().optional(),
  user_role: z.string().min(1, 'Role is required'),
  user_status: z.number().int().min(0).max(2).default(1), // 0 = Inactive, 1 = Active, 2 = Suspended
  can_view_all: z.boolean().optional().default(false),
})

export const updateUserSchema = z.object({
  user_login: z.string().min(1, 'Username is required'),
  user_email: z.string().email('Invalid email address'),
  user_pass: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one digit')
    .regex(/[@$!%*#?&]/, 'Password must contain at least one special character (@$!%*#?&)')
    .optional()
    .or(z.literal('')),
  employee_id: z.string().min(1, 'Employee ID is required'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  position: z.string().optional(),
  user_role: z.string().min(1, 'Role is required'),
  user_status: z.number().int().min(0).max(2).default(1), // 0 = Inactive, 1 = Active, 2 = Suspended
  can_view_all: z.boolean().optional().default(false),
})

export type CreateUserFormData = z.infer<typeof createUserSchema>
export type UpdateUserFormData = z.infer<typeof updateUserSchema>

export const categorySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  description: z.string().optional(),
  parent_id: z.number().optional(),
})

// Category management form schema
export const createCategorySchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Name must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Slug must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  descriptions: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Description must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
  parent_id: z.number().nullable().optional(),
})

export const updateCategorySchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Name must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Slug must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  descriptions: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Description must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
  parent_id: z.number().nullable().optional(),
})

export type CreateCategoryFormData = z.infer<typeof createCategorySchema>
export type UpdateCategoryFormData = z.infer<typeof updateCategorySchema>

export const tagSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  color: z.string().optional(),
})

// Tag management form schema
export const createTagSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Name must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  slug: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Slug must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
  descriptions: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Description must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
})

export const updateTagSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .regex(/^[a-zA-Z0-9,&\-_\s]+$/, 'Name must only contain letters, numbers, spaces, and special characters (&, -, _)'),
  slug: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Slug must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
  descriptions: z
    .string()
    .regex(/^[a-zA-Z0-9,&\-_\s]*$/, 'Description must only contain letters, numbers, spaces, and special characters (&, -, _)')
    .optional()
    .nullable(),
})

export type CreateTagFormData = z.infer<typeof createTagSchema>
export type UpdateTagFormData = z.infer<typeof updateTagSchema>

// Page management form schema
export const createPageSchema = z.object({
  title: z.string().min(1, 'Title is required').max(255, 'Title must not exceed 255 characters'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-zA-Z0-9\-_\/]+$/, 'Slug can only contain letters, numbers, hyphens, underscores, and slashes'),
  content: z.string().optional().nullable(),
  layout_structure: z.any().optional().nullable(),
  layout: z.enum(['default', 'full-width', 'sidebar-left', 'sidebar-right', 'two-column', 'three-column']).default('default'),
  featured_image: z.string().max(500).optional().nullable(),
  meta_title: z.string().max(255).optional().nullable(),
  meta_description: z.string().max(500).optional().nullable(),
  status: z.enum(['draft', 'published', 'scheduled']).default('draft'),
  published_at: z.string().optional().nullable(),
  active: z.boolean().default(true),
})

export const updatePageSchema = z.object({
  title: z.string().min(1, 'Title is required').max(255, 'Title must not exceed 255 characters'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-zA-Z0-9\-_\/]+$/, 'Slug can only contain letters, numbers, hyphens, underscores, and slashes'),
  content: z.string().optional().nullable(),
  layout_structure: z.any().optional().nullable(),
  layout: z.enum(['default', 'full-width', 'sidebar-left', 'sidebar-right', 'two-column', 'three-column']).default('default'),
  featured_image: z.string().max(500).optional().nullable(),
  meta_title: z.string().max(255).optional().nullable(),
  meta_description: z.string().max(500).optional().nullable(),
  status: z.enum(['draft', 'published', 'scheduled']).default('draft'),
  published_at: z.string().optional().nullable(),
  active: z.boolean().default(true),
})

export type CreatePageFormData = z.infer<typeof createPageSchema>
export type UpdatePageFormData = z.infer<typeof updatePageSchema>

export const navigationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  path: z.string().min(1, 'Path is required'),
  icon: z.string().optional(),
  parent_id: z.number().optional(),
  permissions: z.array(z.string()).optional(),
})

// Navigation management form schema
export const createNavigationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  icon: z.string().optional(),
  parent_id: z.number().nullable().optional(),
  active: z.boolean().default(true),
  show_in_menu: z.boolean().default(true),
})

export const updateNavigationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  icon: z.string().optional(),
  parent_id: z.number().nullable().optional(),
  active: z.boolean().default(true),
  show_in_menu: z.boolean().default(true),
})

export type CreateNavigationFormData = z.infer<typeof createNavigationSchema>
export type UpdateNavigationFormData = z.infer<typeof updateNavigationSchema>

// Role management form schema
export const createRoleSchema = z.object({
  name: z.string().min(1, 'Role name is required').max(255, 'Role name must not exceed 255 characters'),
  active: z.boolean().default(true),
  permissions: z.record(
    z.string(), // parent navigation id
    z.record(
      z.string(), // navigation id
      z.record(
        z.string(), // permission name (can_view, can_create, etc.)
        z.boolean()
      )
    )
  ).refine(
    (permissions) => {
      // Check if at least one permission is set to true
      for (const parentNav in permissions) {
        for (const navId in permissions[parentNav]) {
          for (const permName in permissions[parentNav][navId]) {
            if (permissions[parentNav][navId][permName] === true) {
              return true
            }
          }
        }
      }
      return false
    },
    { message: 'Please select at least one permission' }
  ),
})

export const updateRoleSchema = createRoleSchema

export type CreateRoleFormData = z.infer<typeof createRoleSchema>
export type UpdateRoleFormData = z.infer<typeof updateRoleSchema>

export type LoginFormData = z.infer<typeof loginSchema>
export type UserFormData = z.infer<typeof userSchema>
export type CategoryFormData = z.infer<typeof categorySchema>
export type TagFormData = z.infer<typeof tagSchema>
export type NavigationFormData = z.infer<typeof navigationSchema>

